import { HYPERLANE } from './chunk-KGJDYYPB.js';
import { Shield, Droplets, Bot, EyeOff, Server, CandlestickChart, ShieldCheck, Vault, KeyRound, Network, FileCode2, Cpu, Coins, Image, Landmark, Repeat, Shuffle, MessageSquareText, Radar, ArrowLeftRight, Workflow, Layers, Scale, Ship, Milestone, Wind, BadgeCheck, UserCog, FileSignature, Waves, Cake, Route, Cog, Radio, Waypoints } from 'lucide-react';
import { protocolDisplayName, protocolCategory, humanizeFieldName } from '@authe/fe-commons/metadata-analytics';
export { humanizeFieldName } from '@authe/fe-commons/metadata-analytics';
import { resolveScalar } from '@authe/fe-commons/labels';
import * as React from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { cn, CallView, DecodedValue } from '@authe/ui-core';
import { decodedOf, rawOf, isEnriched, parseDecodedCallValue } from '@authe/fe-commons/storage';
import { chainDecodeVocabulary } from '@authe/fe-commons/chains';

// src/registry/registry.ts
var ProtocolUiRegistry = class {
  valueComponents = /* @__PURE__ */ new Map();
  eventComponents = /* @__PURE__ */ new Map();
  callComponents = /* @__PURE__ */ new Map();
  callOwnsReturnsIds = /* @__PURE__ */ new Set();
  valueHints = [];
  eventHints = [];
  callHints = [];
  /** Register a value component under its `componentId` (from a `DisplayHint`). */
  registerValue(componentId, component) {
    this.valueComponents.set(componentId, component);
    return this;
  }
  /** Register an event component under its `componentId` (from an `EventViewHint`). */
  registerEvent(componentId, component) {
    this.eventComponents.set(componentId, component);
    return this;
  }
  /** Register a call component under its `componentId` (from a `CallViewHint`). */
  registerCall(componentId, component, opts) {
    this.callComponents.set(componentId, component);
    if (opts?.ownsReturns) this.callOwnsReturnsIds.add(componentId);
    else this.callOwnsReturnsIds.delete(componentId);
    return this;
  }
  /**
   * Register a bundled protocol's DEFAULT render hints — the FE-side mirror of
   * its backend `display_hints` / `event_views` / `call_views`. Used as a
   * fallback (merged under the body's hints) so bundled cards resolve even when
   * a body carries no `extras.render_hints`.
   */
  registerHints(hints) {
    if (hints.valueHints) this.valueHints.push(...hints.valueHints);
    if (hints.eventHints) this.eventHints.push(...hints.eventHints);
    if (hints.callHints) this.callHints.push(...hints.callHints);
    return this;
  }
  /** The accumulated default hints for every registered bundled protocol. */
  defaultHints() {
    return {
      valueHints: [...this.valueHints],
      eventHints: [...this.eventHints],
      callHints: [...this.callHints]
    };
  }
  /** The value component for a `componentId`, or `undefined` when unregistered. */
  valueComponent(componentId) {
    return componentId ? this.valueComponents.get(componentId) : void 0;
  }
  /** The event component for a `componentId`, or `undefined` when unregistered. */
  eventComponent(componentId) {
    return componentId ? this.eventComponents.get(componentId) : void 0;
  }
  /** The call component for a `componentId`, or `undefined` when unregistered. */
  callComponent(componentId) {
    return componentId ? this.callComponents.get(componentId) : void 0;
  }
  /**
   * True when the call card for `componentId` renders the decoded return value
   * itself (registered with `{ ownsReturns: true }`) — the view layer skips the
   * shared "Returns" footer for it to avoid showing the return twice.
   */
  callOwnsReturns(componentId) {
    return !!componentId && this.callOwnsReturnsIds.has(componentId);
  }
  /** True when a value component is registered for `componentId`. */
  hasValue(componentId) {
    return !!componentId && this.valueComponents.has(componentId);
  }
  /** True when an event component is registered for `componentId`. */
  hasEvent(componentId) {
    return !!componentId && this.eventComponents.has(componentId);
  }
  /** True when a call component is registered for `componentId`. */
  hasCall(componentId) {
    return !!componentId && this.callComponents.has(componentId);
  }
};
function createProtocolUiRegistry() {
  return new ProtocolUiRegistry();
}

// src/theme/tokens.ts
var PROTOCOL_CARD = "rounded-lg border border-border bg-card";
var PROTOCOL_CARD_HEADER = "flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border/60 px-3 py-2";
var PROTOCOL_CARD_BODY = "px-3 py-2.5";
var PROTOCOL_TAG = "inline-flex items-center gap-1 rounded bg-muted px-1.5 py-px text-[11px] font-medium text-muted-foreground";
var FACT_LABEL = "text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
var FACT_VALUE = "min-w-0 break-words text-sm";
var ACCENT_TEXT = {
  neutral: "text-foreground",
  bridge: "text-sky-600 dark:text-sky-400",
  oracle: "text-violet-600 dark:text-violet-400",
  danger: "text-risk-high"
};

// src/theme/brand/accents.ts
var ACCENTS = {
  blue: {
    text: "text-sky-600 dark:text-sky-400",
    badge: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
    border: "border-sky-500/60",
    bar: "border-l-sky-500",
    dot: "bg-sky-500"
  },
  violet: {
    text: "text-violet-600 dark:text-violet-400",
    badge: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
    border: "border-violet-500/60",
    bar: "border-l-violet-500",
    dot: "bg-violet-500"
  },
  indigo: {
    text: "text-indigo-600 dark:text-indigo-400",
    badge: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
    border: "border-indigo-500/60",
    bar: "border-l-indigo-500",
    dot: "bg-indigo-500"
  },
  cyan: {
    text: "text-cyan-600 dark:text-cyan-400",
    badge: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
    border: "border-cyan-500/60",
    bar: "border-l-cyan-500",
    dot: "bg-cyan-500"
  },
  teal: {
    text: "text-teal-600 dark:text-teal-400",
    badge: "bg-teal-500/10 text-teal-700 dark:text-teal-300",
    border: "border-teal-500/60",
    bar: "border-l-teal-500",
    dot: "bg-teal-500"
  },
  amber: {
    text: "text-amber-600 dark:text-amber-400",
    badge: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    border: "border-amber-500/60",
    bar: "border-l-amber-500",
    dot: "bg-amber-500"
  },
  emerald: {
    text: "text-emerald-600 dark:text-emerald-400",
    badge: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    border: "border-emerald-500/60",
    bar: "border-l-emerald-500",
    dot: "bg-emerald-500"
  },
  rose: {
    text: "text-rose-600 dark:text-rose-400",
    badge: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
    border: "border-rose-500/60",
    bar: "border-l-rose-500",
    dot: "bg-rose-500"
  },
  pink: {
    text: "text-pink-600 dark:text-pink-400",
    badge: "bg-pink-500/10 text-pink-700 dark:text-pink-300",
    border: "border-pink-500/60",
    bar: "border-l-pink-500",
    dot: "bg-pink-500"
  },
  orange: {
    text: "text-orange-600 dark:text-orange-400",
    badge: "bg-orange-500/10 text-orange-700 dark:text-orange-300",
    border: "border-orange-500/60",
    bar: "border-l-orange-500",
    dot: "bg-orange-500"
  },
  neutral: {
    text: "text-muted-foreground",
    badge: "bg-muted text-muted-foreground",
    border: "border-border",
    bar: "border-l-border",
    dot: "bg-muted-foreground"
  }
};
var CATEGORY_ACCENT = {
  bridge: "blue",
  oracle: "violet",
  messaging: "cyan",
  aggregator: "teal",
  // DEX / AMM swaps + liquidity (Uniswap & friends) — a hot pink reads as the
  // canonical "swap" colour (Uniswap's own brand) and stays distinct from the
  // teal aggregator routers that wrap them.
  exchange: "pink",
  // Token standards: fungible value reads green; NFTs read amber; the native
  // coin gets its own teal so it stays distinct from an ERC20 (emerald) on a
  // tx that moves both.
  token: "emerald",
  nft: "amber",
  native: "teal",
  // EVM precompiles read as low-level infra — a cool indigo keeps them distinct
  // from the warm token cards without competing for "protocol" colour weight.
  precompile: "indigo",
  // A deployment is low-level infra like a precompile — it puts code on the chain
  // rather than performing a protocol action — so it shares that cool indigo
  // instead of competing with the protocol-action palette.
  deployment: "indigo",
  // Proxy plumbing is transparent infrastructure (it delegates to the real
  // implementation) — neutral grey reads as "structure", not a protocol action,
  // and keeps these frequent cards visually quiet.
  proxy: "neutral",
  // Ownership / access-control is a governance concern, not plumbing — a royal
  // violet reads as "authority" and lifts an `owner()`/`OwnershipTransferred`
  // out of the neutral proxy greys it used to share.
  ownership: "violet",
  // Role-based access control + pausable circuit-breakers — adjacent to ownership
  // but distinct: a warm rose reads as "permissioned action" without colliding
  // with single-owner violet or the warm-amber NFT cards.
  access: "rose",
  // ERC4626 vaults move underlying assets in/out for shares — a cyan keeps the
  // vault distinct from the ERC20 (emerald) it custodies on the same tx.
  vault: "cyan",
  // Lending shares the vault cyan, deliberately. Supplying to Aave is the same
  // reading as depositing to a vault — an asset goes in and a claim on it comes
  // back — and the aToken IS that claim. The debt side is what a vault lacks,
  // but it is the same position seen from the other end, so splitting the hue
  // would separate a story that belongs together. Cyan also keeps a lending
  // card clear of the two things it most often sits beside: the pink of the
  // swap that funded it and the emerald of the ERC20 transfers beneath it.
  lending: "cyan",
  // Smart accounts (Safe & friends): the same authority violet as `ownership`, on
  // purpose. Both answer "who controls this" — a multisig account and an
  // `OwnershipTransferred` are one concern seen from two sides — and giving the
  // account its own hue would split a reading that belongs together.
  safe: "violet",
  // A position whose value derives from something else — prediction-market
  // outcome shares today. Amber, shared with NFTs on purpose: both are holdings
  // whose worth is a claim rather than a balance, and a prediction position is
  // nearer that reading than it is to the pink of a spot venue.
  derivatives: "amber",
  // The chain's own programs and plumbing (Solana's System and Compute Budget
  // programs, a HyperEVM system contract): the cool indigo of the precompile and
  // deployment cards, which are the same kind of thing on EVM.
  infrastructure: "indigo",
  // Shielded pools and ring signatures (Zcash, Monero): what the transaction does
  // NOT show is the finding, so it gets a hue nothing else uses.
  privacy: "orange",
  // A searcher's own program (an arbitrage bot) — not the venue it trades
  // against, so deliberately NOT the exchange pink: rose reads as "someone
  // else's automation acted here", the same attention the access cards ask for.
  mev: "rose",
  neutral: "neutral"
};

// src/protocols/chainlink_ccip/labels.ts
var CHAINLINK_CCIP = {
  protocolId: "chainlink_ccip",
  displayName: "Chainlink CCIP",
  docs: "https://docs.chain.link/ccip"
};
var CCIP_COMPONENTS = {
  /** Value component for the namespaced `chainlink_ccip.curse_subject` kind. */
  curseSubject: "CcipCurseSubject",
  /** Value component for the `message_id` kind — a CCIP explorer deep link. */
  messageId: "CcipMessageId",
  /** Event card for `CCIPSendRequested` / `CCIPMessageSent`. */
  messageSent: "CcipMessageSent",
  /** Call card for `ccipSend` (the OnRamp send entrypoint). */
  send: "CcipSend",
  /** Call card for the OffRamp receive entrypoints (`execute` / `transmit`). */
  executionReport: "CcipExecutionReport",
  /** Value component for a CCV `receipts[]` issuer role badge. */
  receiptRole: "CcipReceiptRole",
  /** Value component for the CCV verification roll-up (verifiers + executor). */
  verification: "CcipVerification",
  /** Value component for the `token_amounts` transfer-list highlight. */
  tokenAmounts: "CcipTokenAmounts"
};
var CCIP_CURSE_SUBJECT_KIND = "chainlink_ccip.curse_subject";
var CCIP_RECEIPT_ROLE_KIND = "chainlink_ccip.receipt_role";
var CCIP_VERIFICATION_KIND = "chainlink_ccip.verification";
var CCIP_TOKEN_TRANSFERS_KIND = "token_transfers";
var CCIP_MESSAGE_ID_KIND = "message_id";
var CCIP_FIELD_LABELS = {
  messageId: "Message ID",
  sender: "Sender",
  receiver: "Receiver",
  recipient: "Receiver",
  sourceChainSelector: "Source chain",
  destChainSelector: "Destination chain",
  destinationChainSelector: "Destination chain",
  feeToken: "Fee token",
  feeTokenAmount: "Fee",
  gasLimit: "Gas limit",
  sequenceNumber: "Sequence",
  nonce: "Nonce",
  tokenAmounts: "Token transfers",
  data: "Payload",
  contract: "Router",
  extraArgs: "Extra args",
  allowOutOfOrderExecution: "Out-of-order execution",
  verification: "Verification",
  receipts: "Fee receipts",
  verifierBlobs: "Verifier attestations"
};
var CCIP_RECEIPT_ROLE_LABELS = {
  verifier: "Verifier",
  executor: "Executor",
  onramp: "OnRamp / Router fee",
  token_pool: "Token pool fee",
  participant: "Participant"
};

// src/protocols/chainlink_automation/labels.ts
var CHAINLINK_AUTOMATION = {
  protocolId: "chainlink_automation",
  displayName: "Chainlink Automation",
  docs: "https://docs.chain.link/chainlink-automation"
};
var AUTOMATION_COMPONENTS = {
  /** Call card for the OCR `transmit` that delivers an `AutomationReport`. */
  transmit: "ChainlinkAutomationTransmit"
};
var AUTOMATION_FIELD_LABELS = {
  registry: "Registry",
  fastGasWei: "Fast gas (wei)",
  linkNative: "LINK / native",
  upkeepId: "Upkeep ID",
  gasLimit: "Gas limit",
  trigger: "Trigger",
  performData: "Perform data"
};

// src/protocols/cowswap/labels.ts
var COWSWAP = {
  protocolId: "cowswap",
  displayName: "CoW Swap",
  docs: "https://docs.cow.fi"
};
var COWSWAP_COMPONENTS = {
  /** The packed 56-byte order uid, as a CoW Explorer deep link. */
  orderUid: "CowOrderUid",
  /** A trade's unpacked flag bitfield, as chips. */
  tradeFlags: "CowTradeFlags",
  /** An expanded order struct. */
  order: "CowOrder",
  /** The off-chain app-data document resolved from its on-chain digest. */
  appData: "CowAppData",
  /** Call cards. */
  settle: "CowSettle",
  createOrder: "CowCreateOrder",
  invalidateOrders: "CowInvalidateOrders",
  /** Event cards. */
  trade: "CowTrade",
  settlement: "CowSettlement",
  interactionLog: "CowInteractionLog",
  orderPlacement: "CowOrderPlacement",
  orderRefund: "CowOrderRefund"
};
var COWSWAP_KINDS = {
  orderUid: "cowswap.order_uid",
  tradeFlags: "cowswap.trade_flags",
  order: "cowswap.order",
  appData: "cowswap.app_data"
};
var COWSWAP_CALLS = {
  settle: "settle",
  createOrder: "createOrder",
  invalidateOrders: "invalidateOrdersIgnoringNotAllowed"
};
var COWSWAP_EVENTS = {
  trade: "Trade",
  settlement: "Settlement",
  interaction: "Interaction",
  orderPlacement: "OrderPlacement",
  orderRefund: "OrderRefund"
};
var COWSWAP_FIELD_LABELS = {
  appCode: "Placed via",
  orderClass: "Order class",
  partnerFeeBps: "Partner fee (bps)",
  slippageBps: "Slippage (bps)",
  digest: "App-data digest",
  source: "Document source"
};

// src/protocols/kyberswap/labels.ts
var KYBERSWAP = {
  protocolId: "kyberswap_meta_aggregator",
  displayName: "KyberSwap",
  docs: "https://docs.kyberswap.com"
};
var KYBERSWAP_COMPONENTS = {
  /** The integrator's own JSON, carried on-chain beside the trade. */
  clientData: "KyberSwapClientData",
  swap: "KyberSwapSwap",
  swapped: "KyberSwapSwapped",
  exchange: "KyberSwapExchange",
  fee: "KyberSwapFee",
  error: "KyberSwapError"
};
var KYBERSWAP_KINDS = {
  clientData: "kyberswap.client_data"
};
var KYBERSWAP_CALLS = {
  swap: "swap"
};
var KYBERSWAP_EVENTS = {
  swapped: "Swapped",
  exchange: "Exchange",
  fee: "Fee",
  error: "Error"
};
var KYBERSWAP_CLIENT_DATA_FIELDS = {
  source: "source",
  amountInUsd: "amountInUsd",
  amountOutUsd: "amountOutUsd",
  quotedAmountOut: "quotedAmountOut",
  routeId: "routeId"
};
var KYBERSWAP_FIELD_LABELS = {
  source: "Integrator",
  amountInUsd: "Value in (USD)",
  amountOutUsd: "Value out (USD)",
  quotedAmountOut: "Quoted output",
  routeId: "Route ID"
};

// src/protocols/lifi/labels.ts
var LIFI = {
  protocolId: "lifi",
  displayName: "LI.FI",
  docs: "https://docs.li.fi"
};
var LIFI_COMPONENTS = {
  /** Call card for the LI.FI Diamond Mayan-facet bridge entrypoints (swap + no-swap). */
  bridge: "LiFiBridge"
};
var LIFI_BRIDGE_CALLS = [
  "swapAndStartBridgeTokensViaMayan",
  "startBridgeTokensViaMayan"
];
var LIFI_FIELD_LABELS = {
  // BridgeData envelope.
  bridge: "Bridge",
  bridgedToken: "Bridged token",
  minAmount: "Bridged amount",
  recipient: "Recipient",
  destChain: "Destination chain",
  transactionId: "Transfer ID",
  // Source DEX swap (SwapData[0]).
  fromToken: "From token",
  fromAmount: "From amount",
  toToken: "To token",
  swapRouter: "Swap router",
  // MayanData (the wrapped bridge).
  mayanProtocol: "Mayan protocol"
};

// src/protocols/mayan/labels.ts
var MAYAN = {
  protocolId: "mayan",
  displayName: "Mayan",
  docs: "https://docs.mayan.finance"
};
var MAYAN_COMPONENTS = {
  /** Call card for the shared Forwarder front door (`forward*` / `swapAndForward*`). */
  forwarder: "MayanForwarder",
  /** Call card for a Swift order verb (`createOrderWith*` / `fulfill*` / `unlock*`). */
  swiftOrder: "MayanSwiftOrder",
  /** Call card for the Swift v2 driver batch executor (`executeAndRescue`). */
  executor: "MayanExecuteAndRescue",
  /** Event card for a Swift order-lifecycle event (`OrderCreated` / `OrderFulfilled` / …). */
  orderEvent: "MayanOrderEvent",
  /** Value card for the namespaced `mayan.packed_payload` kind. */
  packedPayload: "MayanPackedPayload",
  /** Value card for the namespaced `mayan.swift_message` kind (Swift VAA payload). */
  swiftMessage: "MayanSwiftMessage",
  /** Value card for the Mayan order hash surfaced as the `message_id` highlight. */
  orderId: "MayanOrderId"
};
var MAYAN_MESSAGE_ID_KIND = "message_id";
var MAYAN_PACKED_PAYLOAD_KIND = "mayan.packed_payload";
var MAYAN_SWIFT_MESSAGE_KIND = "mayan.swift_message";
var MAYAN_FIELD_LABELS = {
  contract: "Contract",
  // Swift VAA message (the `encodedVm` payload an unlock / fulfill / refund consumes).
  action: "Action",
  driver: "Driver",
  referrerBps: "Referrer fee (bps)",
  protocolBps: "Protocol fee (bps)",
  fulfillTime: "Fulfilled at",
  // Forwarder envelope.
  tokenIn: "Input token",
  amountIn: "Input amount",
  middleToken: "Bridged token",
  minMiddleAmount: "Min bridged amount",
  mayanProtocol: "Mayan protocol",
  swapProtocol: "Swap router",
  // Swift order intent.
  trader: "Trader",
  recipient: "Recipient",
  token: "Token",
  tokenOut: "Delivered token",
  amount: "Amount",
  minAmountOut: "Min received",
  destChain: "Destination chain",
  sourceChain: "Source chain",
  deadline: "Deadline",
  referrer: "Referrer",
  gasDrop: "Gas drop",
  orderHash: "Order hash",
  // Swift v2 driver `executeAndRescue` batch.
  calls: "Calls",
  target: "Target",
  value: "Value",
  callData: "Call",
  rescueReceiver: "Rescue receiver",
  rescueTokens: "Rescued tokens"
};
var MAYAN_FORWARDER_CALLS = [
  "forwardEth",
  "forwardERC20",
  "swapAndForwardEth",
  "swapAndForwardERC20"
];
var MAYAN_SWIFT_CALLS = [
  "createOrderWithEth",
  "createOrderWithToken",
  "fulfillOrder",
  "fulfillSimple",
  "unlockSingle",
  "unlockBatch"
];
var MAYAN_EXECUTOR_CALLS = ["executeAndRescue"];
var MAYAN_ORDER_EVENTS = [
  "OrderCreated",
  "OrderFulfilled",
  "OrderUnlocked",
  "OrderCanceled",
  "OrderRefunded"
];

// src/protocols/okx_xbridge/labels.ts
var OKX_XBRIDGE = {
  protocolId: "okx_xbridge",
  displayName: "OKX XBridge",
  docs: "https://www.okx.com/web3/build/docs/waas/dex-crosschain-api"
};
var OKX_COMPONENTS = {
  /** Call card for the XBridge swap-and-bridge entrypoints (single + batch). */
  swap: "OkxXBridgeSwap",
  /** Value card for the namespaced `okx_xbridge.packed_payload` kind. */
  packedPayload: "OkxPackedPayload"
};
var OKX_PACKED_PAYLOAD_KIND = "okx_xbridge.packed_payload";
var OKX_FIELD_LABELS = {
  // Top-level entrypoint.
  refId: "Reference ID",
  // BridgeRequest leg.
  token: "Bridged token",
  amount: "Input amount",
  minReturnAmount: "Bridged amount",
  recipient: "Recipient",
  destChain: "Destination chain",
  fee: "Fee recipient",
  // Inner OKX DexRouter swap.
  swapMethod: "Swap method",
  fromToken: "From token",
  fromTokenAmount: "From amount",
  toToken: "To token",
  receiver: "Swap receiver"
};

// src/protocols/oneinch/labels.ts
var ONEINCH = {
  protocolId: "oneinch",
  displayName: "1inch",
  docs: "https://docs.1inch.io"
};
var ONEINCH_COMPONENTS = {
  /** An address unpacked from the low 160 bits of a `uint256` argument. */
  address: "OneInchAddress",
  /** One route hop: pool + AMM family + direction, from a single word. */
  pool: "OneInchPool",
  makerTraits: "OneInchMakerTraits",
  takerTraits: "OneInchTakerTraits",
  swap: "OneInchSwap",
  unoswap: "OneInchUnoswap",
  fillOrder: "OneInchFillOrder",
  cancelOrder: "OneInchCancelOrder",
  orderFilled: "OneInchOrderFilled",
  orderCancelled: "OneInchOrderCancelled",
  epochIncreased: "OneInchEpochIncreased",
  // --- The pre-V6 generations. V2 through V5 are still live contracts with
  // live liquidity, and they answer DIFFERENT verbs than V6 does, so each needs
  // its own id rather than being folded into the V6 cards above.
  /** `uniswapV3Swap` / `unoswap` on V3-V5: a route given as packed pool words. */
  legacyRoute: "OneInchLegacyRoute",
  /** `clipperSwap`: a single-venue RFQ-style swap, not a route. */
  clipperSwap: "OneInchClipperSwap",
  /** `fillOrderRFQ` on Limit Order Protocol V1/V2. */
  rfqFill: "OneInchRfqFill",
  /** `settleOrders` on the Fusion settlement contract. */
  fusionSettle: "OneInchFusionSettle",
  /** The `Swapped` event — V2/V3 routers, and the shape KyberSwap forked. */
  swapped: "OneInchSwapped",
  rfqOrderFilled: "OneInchOrderFilledRfq",
  orderCanceled: "OneInchOrderCanceled",
  nonceIncreased: "OneInchNonceIncreased"
};
var ONEINCH_KINDS = {
  address: "oneinch.address",
  dex: "oneinch.dex",
  makerTraits: "oneinch.maker_traits",
  takerTraits: "oneinch.taker_traits"
};
var ONEINCH_CALLS = {
  swap: "swap",
  unoswap: "unoswap",
  fillOrder: "fillOrder",
  cancelOrder: "cancelOrder",
  uniswapV3Swap: "uniswapV3Swap",
  clipperSwap: "clipperSwap",
  fillOrderRfq: "fillOrderRFQ",
  settleOrders: "settleOrders"
};
var ONEINCH_EVENTS = {
  orderFilled: "OrderFilled",
  orderCancelled: "OrderCancelled",
  epochIncreased: "EpochIncreased",
  /**
   * The V2/V3 router settlement event. NOTE the spelling of the two cancel
   * events: the Limit Order Protocol spells it `OrderCancelled` (two Ls) and the
   * RFQ contract spells it `OrderCanceled` (one). They are different events on
   * different contracts, not a typo to normalise away.
   */
  swapped: "Swapped",
  rfqOrderFilled: "OrderFilledRFQ",
  orderCanceled: "OrderCanceled",
  nonceIncreased: "NonceIncreased"
};
var ONEINCH_FIELDS = {
  address: "address",
  poolProtocol: "poolProtocol",
  zeroForOne: "zeroForOne"
};

// src/protocols/conditional_tokens/labels.ts
var CONDITIONAL_TOKENS = {
  protocolId: "conditional_tokens",
  displayName: "Conditional Tokens",
  docs: "https://docs.gnosis.io/conditionaltokens/"
};
var CONDITIONAL_TOKENS_COMPONENTS = {
  position: "ConditionalTokensPosition"
};
var CONDITIONAL_TOKENS_EVENTS = {
  positionSplit: "PositionSplit",
  positionsMerge: "PositionsMerge",
  payoutRedemption: "PayoutRedemption"
};
var CONDITIONAL_TOKENS_FIELDS = {
  stakeholder: "stakeholder",
  collateralToken: "collateralToken",
  conditionId: "conditionId",
  parentCollectionId: "parentCollectionId",
  /** `PositionSplit` / `PositionsMerge` — which outcomes the stake was split across. */
  partition: "partition",
  /**
   * `PayoutRedemption` — which outcomes were CLAIMED. The standard's own name,
   * and a different concept from `partition`, which is why the redemption does
   * not carry one. The card reads both so a redemption still shows its
   * outcome count.
   */
  indexSets: "indexSets",
  amount: "amount",
  payout: "payout"
};
var CONDITIONAL_TOKENS_LABELS = {
  trader: "Trader",
  market: "Market",
  collateral: "Collateral",
  staked: "Staked",
  returned: "Returned",
  paidOut: "Paid out",
  outcomes: "Outcomes"
};
var CONDITIONAL_TOKENS_VERBS = {
  [CONDITIONAL_TOKENS_EVENTS.positionSplit]: {
    title: "Open prediction-market position",
    amountLabel: CONDITIONAL_TOKENS_LABELS.staked,
    note: "Collateral in, one outcome share out per outcome."
  },
  [CONDITIONAL_TOKENS_EVENTS.positionsMerge]: {
    title: "Close prediction-market position",
    amountLabel: CONDITIONAL_TOKENS_LABELS.returned,
    note: "A complete set of outcome shares back, collateral out."
  },
  [CONDITIONAL_TOKENS_EVENTS.payoutRedemption]: {
    title: "Redeem resolved market",
    amountLabel: CONDITIONAL_TOKENS_LABELS.paidOut,
    note: "The market RESOLVED and this holder claimed. May be zero."
  }
};

// src/protocols/erc4337/labels.ts
var ERC4337 = {
  protocolId: "erc4337",
  displayName: "ERC-4337",
  docs: "https://eips.ethereum.org/EIPS/eip-4337"
};
var ERC4337_COMPONENTS = {
  userOperation: "Erc4337UserOperation",
  handleOps: "Erc4337HandleOps"
};
var ERC4337_EVENTS = { userOperationEvent: "UserOperationEvent" };
var ERC4337_CALLS = { handleOps: "handleOps" };
var ERC4337_FIELDS = {
  sender: "sender",
  paymaster: "paymaster",
  nonce: "nonce",
  success: "success",
  actualGasCost: "actualGasCost",
  actualGasUsed: "actualGasUsed",
  userOpHash: "userOpHash"
};
var ERC4337_LABELS = {
  account: "Smart account",
  entryPoint: "EntryPoint",
  paidBy: "Paid by",
  selfFunded: "Self-funded",
  gasCost: "Gas cost",
  userOpHash: "Operation hash",
  nonce: "Nonce"
};

// src/protocols/layerzero_v2/labels.ts
var LAYERZERO_V2 = {
  protocolId: "layerzero_v2",
  displayName: "LayerZero V2",
  docs: "https://docs.layerzero.network/v2"
};
var LAYERZERO_V2_COMPONENTS = {
  payloadVerified: "LayerZeroPayloadVerified"
};
var LAYERZERO_V2_EVENTS = { payloadVerified: "PayloadVerified" };
var LAYERZERO_V2_FIELDS = {
  dvn: "dvn",
  confirmations: "confirmations",
  proofHash: "proofHash"
};
var LAYERZERO_V2_LABELS = {
  verifier: "Verifier (DVN)",
  library: "Receive library",
  confirmations: "Confirmations waited",
  proofHash: "Payload hash",
  oneOfQuorum: "One attestation"
};

// src/protocols/relay/labels.ts
var RELAY = {
  protocolId: "relay",
  displayName: "Relay"
};
var RELAY_EVENTS = {
  fundsMovement: "FundsMovement"
};
var RELAY_COMPONENTS = {
  fundsMovement: "RelayFundsMovement"
};
var RELAY_FIELDS = {
  from: ["from", "sender", "depositor"],
  to: ["to", "recipient", "receiver"],
  token: ["token", "currency"],
  amount: ["amount", "value"],
  tag: ["data"]
};
var RELAY_LABELS = {
  movedBy: "Moved by",
  from: "From",
  to: "To",
  amount: "Amount",
  restates: "Restates an ERC-20 Transfer in this transaction"
};

// src/protocols/pancakeswap/labels.ts
var PANCAKESWAP = {
  protocolId: "pancakeswap_router",
  displayName: "PancakeSwap",
  docs: "https://docs.pancakeswap.finance"
};
var PANCAKESWAP_COMPONENTS = {
  /** A V3 packed path, unpacked into `TOKEN --fee--> TOKEN` hops. */
  path: "PancakeSwapPath",
  /** One Universal Router command: its name, its place in the plan, its args. */
  command: "PancakeSwapCommand",
  /** An Infinity pool identity — currencies, hook, fee — with no pool address. */
  poolKey: "PancakeSwapPoolKey",
  v2Swap: "PancakeSwapV2Swap",
  v3Swap: "PancakeSwapV3Swap",
  multicall: "PancakeSwapMulticall",
  execute: "PancakeSwapExecute",
  swapEvent: "PancakeSwapSwapEvent"
};
var PANCAKESWAP_KINDS = {
  path: "pancakeswap.v3_path",
  command: "pancakeswap.command",
  poolKey: "pancakeswap.pool_key"
};
var PANCAKESWAP_CALLS = {
  swapExactTokensForTokens: "swapExactTokensForTokens",
  exactInput: "exactInput",
  exactInputSingle: "exactInputSingle",
  multicall: "multicall",
  execute: "execute"
};
var PANCAKESWAP_EVENTS = {
  swap: "Swap"
};
var PANCAKESWAP_FIELDS = {
  path: "path",
  fee: "fee",
  commandName: "commandName",
  commandIndex: "commandIndex",
  currency0: "currency0",
  currency1: "currency1",
  hooks: "hooks",
  poolManager: "poolManager"
};

// src/protocols/pancakeswap_v3_pool/labels.ts
var PANCAKESWAP_V3_POOL = {
  protocolId: "pancakeswap_v3_pool",
  displayName: "PancakeSwap V3",
  docs: "https://docs.pancakeswap.finance/developers/smart-contracts/pancakeswap-exchange/v3-contracts"
};
var PANCAKESWAP_V3_POOL_COMPONENTS = {
  poolSwap: "PancakeSwapPoolSwap"
};
var PANCAKESWAP_V3_POOL_EVENTS = {
  swap: "Swap"
};
var PANCAKESWAP_V3_POOL_FIELDS = {
  sender: "sender",
  recipient: "recipient",
  amount0: "amount0",
  amount1: "amount1",
  sqrtPriceX96: "sqrtPriceX96",
  liquidity: "liquidity",
  tick: "tick",
  protocolFeesToken0: "protocolFeesToken0",
  protocolFeesToken1: "protocolFeesToken1"
};
var PANCAKESWAP_V3_POOL_LABELS = {
  pool: "Pool",
  trader: "Trader",
  recipient: "Received by",
  soldToken0: "Sold (token0)",
  soldToken1: "Sold (token1)",
  boughtToken0: "Bought (token0)",
  boughtToken1: "Bought (token1)",
  protocolFees: "Protocol fees",
  tick: "Tick",
  liquidity: "Liquidity"
};

// src/protocols/sushiswap/labels.ts
var SUSHISWAP = {
  protocolId: "sushiswap_router",
  displayName: "SushiSwap",
  docs: "https://docs.sushi.com"
};
var SUSHISWAP_COMPONENTS = {
  /** A `RouteProcessorData` struct SushiXSwapV2 forwards (source or dest leg). */
  routeProcessorData: "SushiSwapRouteProcessorData",
  snwapMultiple: "SushiSwapSnwapMultiple",
  route: "SushiSwapRoute",
  bridgeOnSource: "SushiSwapBridgeOnSource"
};
var SUSHISWAP_KINDS = {
  routeProcessorData: "sushiswap.route_processor_data"
};
var SUSHISWAP_CALLS = {
  snwapMultiple: "snwapMultiple"
};
var SUSHISWAP_EVENTS = {
  route: "Route",
  swapAndBridgeOnSource: "SwapAndBridgeOnSource"
};

// src/protocols/tokemak_messaging/labels.ts
var TOKEMAK_MESSAGING = {
  protocolId: "tokemak_messaging",
  displayName: "Tokemak Messaging",
  docs: "https://docs.tokemak.xyz"
};
var TOKEMAK_COMPONENTS = {
  /** Bespoke event card for the ReceivingRouter `MessageData` inbound message. */
  message: "TokemakMessage"
};
var TOKEMAK_FIELD_LABELS = {
  messageType: "Payload type",
  sourceChainSelector: "Source chain",
  messageOrigin: "Origin",
  messageReceiver: "Receiver",
  token: "Token",
  amount: "Amount",
  messageNonce: "Nonce",
  ccipMessageId: "CCIP message ID"
};

// src/protocols/wormhole/labels.ts
var WORMHOLE = {
  protocolId: "wormhole",
  displayName: "Wormhole",
  docs: "https://docs.wormhole.com"
};
var WORMHOLE_COMPONENTS = {
  /** Value component for the namespaced `wormhole.vaa` kind. */
  vaa: "WormholeVaa",
  /** Value component for the Executor signed quote (`wormhole.signed_quote`). */
  signedQuote: "WormholeSignedQuote",
  /** Value component for the Executor request (`wormhole.executor_request`). */
  executorRequest: "WormholeExecutorRequest",
  /** Value component for the relay instructions (`wormhole.relay_instructions`). */
  relayInstructions: "WormholeRelayInstructions",
  /** Value component for the Token Bridge transfer payload. */
  transferPayload: "WormholeTransferPayload",
  /** Value component for the canonical `message_id` kind — a WormholeScan deep
   * link for a VAA id (`emitterChain/emitter/sequence`). */
  messageId: "WormholeMessageId",
  /** Call component for the Core Bridge `parseVM` / `parseAndVerifyVM` reads —
   * renders the parsed VAA struct (+ verification result), and the `encodedVM`
   * argument whether it stayed raw bytes or was itself decoded to a VAA. */
  parseVm: "WormholeParseVM"
};
var WORMHOLE_VAA_KIND = "wormhole.vaa";
var WORMHOLE_SIGNED_QUOTE_KIND = "wormhole.signed_quote";
var WORMHOLE_EXECUTOR_REQUEST_KIND = "wormhole.executor_request";
var WORMHOLE_RELAY_INSTRUCTIONS_KIND = "wormhole.relay_instructions";
var WORMHOLE_TRANSFER_PAYLOAD_KIND = "wormhole.transfer_payload";
var WORMHOLE_MESSAGE_ID_KIND = "message_id";
var WORMHOLE_VAA_ID_KIND = "vaa_id";
var WORMHOLE_FIELD_LABELS = {
  vaaId: "VAA ID",
  emitterChain: "Emitter chain",
  emitterAddress: "Emitter",
  sequence: "Sequence",
  consistencyLevel: "Finality",
  guardianSet: "Guardian set",
  signatures: "Guardian signatures",
  payloadType: "Payload",
  amount: "Amount",
  token: "Token",
  tokenChain: "Token chain",
  recipient: "Recipient",
  recipientChain: "Destination chain",
  payloadHex: "Raw payload"
};
var WORMHOLE_VM_FIELD_LABELS = {
  version: "Version",
  timestamp: "Timestamp",
  nonce: "Nonce",
  emitterChain: "Emitter chain",
  emitterAddress: "Emitter",
  sequence: "Sequence",
  consistencyLevel: "Finality",
  guardianSet: "Guardian set",
  signatures: "Guardian signatures",
  hash: "VAA hash",
  payload: "Signed payload",
  valid: "Verification",
  reason: "Reason",
  encodedVaa: "Encoded VAA"
};
var WORMHOLE_QUOTE_FIELD_LABELS = {
  prefix: "Quote version",
  quoterAddress: "Quoter",
  payeeAddress: "Payee",
  srcChain: "Source chain",
  dstChain: "Destination chain",
  baseFee: "Base fee",
  dstGasPrice: "Dest gas price",
  srcPrice: "Source price",
  dstPrice: "Dest price",
  expiryTime: "Expires",
  signature: "Signature"
};
var WORMHOLE_EXECUTOR_FIELD_LABELS = {
  prefix: "Request type",
  autoDiscover: "Auto-discover",
  type: "Type",
  gasLimit: "Gas limit",
  msgValue: "Message value",
  amount: "Drop-off amount",
  recipient: "Recipient"
};
var WORMHOLE_REQUEST_PREFIX_LABELS = {
  ERV1: "Deliver VAA",
  ERN1: "Deliver NTT",
  ERC1: "Deliver CCTP v1",
  ERC2: "Deliver CCTP v2"
};

// src/protocols/uniswap/labels.ts
var UNISWAP_UNIVERSAL_ROUTER = {
  protocolId: "uniswap_universal_router",
  displayName: "Uniswap Universal Router",
  docs: "https://docs.uniswap.org/contracts/universal-router/overview"
};
var UNISWAP_PERMIT2 = {
  protocolId: "uniswap_permit2",
  displayName: "Permit2",
  docs: "https://docs.uniswap.org/contracts/permit2/overview"
};
var UNISWAP_V1_FACTORY = {
  protocolId: "uniswap_v1_factory",
  displayName: "Uniswap V1",
  docs: "https://docs.uniswap.org/contracts/v1/overview"
};
var UNISWAP_V1_EXCHANGE = {
  protocolId: "uniswap_v1_exchange",
  displayName: "Uniswap V1",
  docs: "https://docs.uniswap.org/contracts/v1/overview"
};
var UNISWAP_V2_ROUTER = {
  protocolId: "uniswap_v2_router",
  displayName: "Uniswap V2",
  docs: "https://docs.uniswap.org/contracts/v2/overview"
};
var UNISWAP_V3_ROUTER = {
  protocolId: "uniswap_v3_router",
  displayName: "Uniswap V3",
  docs: "https://docs.uniswap.org/contracts/v3/overview"
};
var UNISWAP_V3_POSITION_MANAGER = {
  protocolId: "uniswap_v3_position_manager",
  displayName: "Uniswap V3 Positions",
  docs: "https://docs.uniswap.org/contracts/v3/reference/periphery/NonfungiblePositionManager"
};
var UNISWAP_V4_POOL_MANAGER = {
  protocolId: "uniswap_v4",
  displayName: "Uniswap V4",
  docs: "https://docs.uniswap.org/contracts/v4/overview"
};
var UNISWAP_V4_POSITION_MANAGER = {
  protocolId: "uniswap_v4_position_manager",
  displayName: "Uniswap V4 Positions",
  docs: "https://docs.uniswap.org/contracts/v4/reference/periphery/PositionManager"
};
var UNISWAP_V2_FACTORY = {
  protocolId: "uniswap_v2_factory",
  displayName: "Uniswap V2",
  docs: "https://docs.uniswap.org/contracts/v2/overview"
};
var UNISWAP_V3_FACTORY = {
  protocolId: "uniswap_v3_factory",
  displayName: "Uniswap V3",
  docs: "https://docs.uniswap.org/contracts/v3/overview"
};
var UNISWAP_V2_PAIR = {
  protocolId: "uniswap_v2_pair",
  displayName: "Uniswap V2",
  docs: "https://docs.uniswap.org/contracts/v2/concepts/protocol-overview/smart-contracts"
};
var UNISWAP_V3_POOL = {
  protocolId: "uniswap_v3_pool",
  displayName: "Uniswap V3",
  docs: "https://docs.uniswap.org/contracts/v3/reference/core/UniswapV3Pool"
};
var UNISWAP_BRAND_LIST = [
  UNISWAP_UNIVERSAL_ROUTER,
  UNISWAP_PERMIT2,
  UNISWAP_V1_FACTORY,
  UNISWAP_V1_EXCHANGE,
  UNISWAP_V2_ROUTER,
  UNISWAP_V3_ROUTER,
  UNISWAP_V3_POSITION_MANAGER,
  UNISWAP_V4_POOL_MANAGER,
  UNISWAP_V4_POSITION_MANAGER,
  UNISWAP_V2_FACTORY,
  UNISWAP_V3_FACTORY,
  UNISWAP_V2_PAIR,
  UNISWAP_V3_POOL
];
var UNISWAP_COMPONENTS = {
  /** Universal Router `execute` — the command-leg multicall card. */
  universalRouter: "UniswapUniversalRouter",
  /** A V1/V2/V3 router swap call (exact-in / exact-out, single or multi-hop). */
  swap: "UniswapSwap",
  /** A liquidity call — V1 add/remove, V3/V4 position manager verbs. */
  liquidity: "UniswapLiquidity",
  /** A Permit2 approval / transfer call. */
  permit2: "UniswapPermit2",
  /** A pool event — reserved for when the backend attributes pool contracts. */
  poolEvent: "UniswapPoolEvent"
};
var UNISWAP_FIELD_LABELS = {
  contract: "Contract",
  // Swap legs.
  tokenIn: "From token",
  tokenOut: "To token",
  amountIn: "Amount in",
  amountInMax: "Max in",
  amountOut: "Amount out",
  amountOutMin: "Min out",
  recipient: "Recipient",
  payer: "Payer is user",
  path: "Route",
  fee: "Pool fee",
  sqrtPriceLimit: "Price limit (sqrtX96)",
  deadline: "Deadline",
  // V4 PoolManager swap (PoolKey + SwapParams).
  currency0: "Currency 0",
  currency1: "Currency 1",
  zeroForOne: "Direction (0\u21921)",
  amountSpecified: "Amount specified",
  hooks: "Hooks",
  swapDelta: "Swap delta",
  // Permit2.
  token: "Token",
  spender: "Spender",
  amount: "Amount",
  expiration: "Expiration",
  nonce: "Nonce",
  sigDeadline: "Signature deadline",
  owner: "Owner",
  from: "From",
  to: "To",
  // Liquidity.
  token0: "Token 0",
  token1: "Token 1",
  tokenId: "Position ID",
  liquidity: "Liquidity",
  tickLower: "Tick lower",
  tickUpper: "Tick upper",
  amount0: "Amount 0",
  amount1: "Amount 1",
  amount0Min: "Min amount 0",
  amount1Min: "Min amount 1",
  // Pool events.
  sender: "Sender",
  amount0In: "Amount 0 in",
  amount1In: "Amount 1 in",
  amount0Out: "Amount 0 out",
  amount1Out: "Amount 1 out",
  tick: "Tick",
  commands: "Commands"
};
var UNISWAP_COMMAND_LABELS = {
  V3_SWAP_EXACT_IN: "V3 swap \xB7 exact in",
  V3_SWAP_EXACT_OUT: "V3 swap \xB7 exact out",
  V2_SWAP_EXACT_IN: "V2 swap \xB7 exact in",
  V2_SWAP_EXACT_OUT: "V2 swap \xB7 exact out",
  PERMIT2_TRANSFER_FROM: "Permit2 transfer",
  PERMIT2_TRANSFER_FROM_BATCH: "Permit2 transfer (batch)",
  PERMIT2_PERMIT: "Permit2 permit",
  PERMIT2_PERMIT_BATCH: "Permit2 permit (batch)",
  SWEEP: "Sweep",
  TRANSFER: "Transfer",
  PAY_PORTION: "Pay portion",
  WRAP_ETH: "Wrap ETH",
  UNWRAP_WETH: "Unwrap WETH",
  BALANCE_CHECK_ERC20: "Balance check",
  APPROVE_ERC20: "Approve ERC20",
  SEAPORT_V1_5: "Buy NFT \xB7 Seaport 1.5",
  SEAPORT_V1_4: "Buy NFT \xB7 Seaport 1.4",
  LOOKS_RARE_V2: "Buy NFT \xB7 LooksRare",
  X2Y2_721: "Buy NFT \xB7 X2Y2",
  X2Y2_1155: "Buy NFT \xB7 X2Y2",
  NFTX: "Buy NFT \xB7 NFTX",
  NFT20: "Buy NFT \xB7 NFT20",
  CRYPTOPUNKS: "Buy NFT \xB7 CryptoPunks",
  SUDOSWAP: "Buy NFT \xB7 sudoswap",
  FOUNDATION: "Buy NFT \xB7 Foundation",
  ELEMENT_MARKET: "Buy NFT \xB7 Element",
  EXECUTE_SUB_PLAN: "Sub-plan"
};
var UNISWAP_SWAP_COMMANDS = /* @__PURE__ */ new Set([
  "V3_SWAP_EXACT_IN",
  "V3_SWAP_EXACT_OUT",
  "V2_SWAP_EXACT_IN",
  "V2_SWAP_EXACT_OUT"
]);

// src/standards/erc20/labels.ts
var ERC20 = {
  protocolId: "erc20",
  displayName: "ERC20",
  docs: "https://eips.ethereum.org/EIPS/eip-20"
};

// src/standards/erc721/labels.ts
var ERC721 = {
  protocolId: "erc721",
  displayName: "ERC721",
  docs: "https://eips.ethereum.org/EIPS/eip-721"
};

// src/standards/erc1155/labels.ts
var ERC1155 = {
  protocolId: "erc1155",
  displayName: "ERC1155",
  docs: "https://eips.ethereum.org/EIPS/eip-1155"
};

// src/standards/native/labels.ts
var NATIVE_TRANSFER = {
  protocolId: "native_transfer",
  displayName: "Native transfer"
};

// src/standards/precompiles/labels.ts
var PRECOMPILE = {
  protocolId: "precompile",
  displayName: "Precompile"
};
var PRECOMPILE_COMPONENT = "PrecompileCall";
var PRECOMPILES = {
  ecrecover: "ECDSA signature recovery",
  sha256: "SHA-256 hash",
  ripemd160: "RIPEMD-160 hash",
  identity: "Identity (verbatim data copy)",
  modexp: "Modular exponentiation",
  ecadd: "BN254 point addition",
  ecmul: "BN254 scalar multiplication",
  ecpairing: "BN254 pairing check",
  blake2f: "BLAKE2b F compression",
  pointEvaluation: "KZG point evaluation (EIP-4844)"
};

// src/standards/lowlevel/labels.ts
var LOWLEVEL = {
  protocolId: "lowlevel",
  displayName: "Low-level"
};
var LOWLEVEL_COMPONENT = "LowLevelCall";
var LOWLEVEL_CALLS = {
  receive: {
    kind: "receive",
    caption: "Native value received",
    description: "Empty calldata carrying native value: the EVM dispatched this call to the contract's receive() function. The transferred amount appears as the accompanying native transfer."
  },
  fallback: {
    kind: "fallback",
    caption: "Catch-all entry point",
    description: "Empty calldata with no matching function: the EVM dispatched this call to the contract's fallback() function."
  }
};

// src/standards/deployment/labels.ts
var DEPLOYMENT = {
  protocolId: "deployment",
  displayName: "Deployment"
};
var CONTRACT_CREATION_CALL = "ContractCreation";
var INIT_CODE_PARAM = "init_code";
var CONTRACT_TYPE_PARAM = "contract_type";
var CODE_HASH_PARAM = "code_hash";
var PROTOCOL_ID_PARAM = "protocol_id";
var IDENTIFIED_BY_PARAM = "identified_by";
var IDENTIFIED_BY = {
  /** Init-code hash matched a known contract AND its canonical factory deployed it. */
  codeHash: "code_hash",
  /** A protocol definition pins the account that ran the opcode. */
  deployer: "deployer",
  /** Byte-identical to a known contract, but deployed by something else. */
  codeHashUnverified: "code_hash_unverified",
  /** The bytecode implements a standard interface in full — shape, not authorship. */
  interface: "interface"
};
var INITIAL_SUPPLY_PARAM = "initial_supply";
var MINTED_TO_PARAM = "minted_to";
var OWNER_PARAM = "owner";
var OWNERSHIP_RENOUNCED_PARAM = "ownership_renounced";
var TOKEN0_PARAM = "token0";
var TOKEN1_PARAM = "token1";
var FEE_TIER_PARAM = "fee_tier";
var TICK_SPACING_PARAM = "tick_spacing";
var FUNDED_WITH_PARAM = "funded_with";

// src/standards/proxy/labels.ts
var PROXY = {
  protocolId: "proxy",
  displayName: "Proxy"
};

// src/standards/ownership/labels.ts
var OWNERSHIP = {
  protocolId: "ownership",
  displayName: "Ownership"
};

// src/standards/access/labels.ts
var ACCESS = {
  protocolId: "access",
  displayName: "Access control"
};

// src/standards/erc4626/labels.ts
var ERC4626 = {
  protocolId: "erc4626",
  displayName: "Vault (ERC-4626)",
  docs: "https://eips.ethereum.org/EIPS/eip-4626"
};
var VAULT_COMPONENTS = {
  /** A vault entrypoint or view call (`deposit`/`mint`/`withdraw`/`redeem`/`totalAssets`/…). */
  call: "VaultCall",
  /** A vault log (`Deposit`/`Withdraw`). */
  event: "VaultEvent"
};

// src/protocols/aave_v3/labels.ts
var AAVE_V3 = {
  protocolId: "aave_v3",
  displayName: "Aave V3",
  docs: "https://aave.com/docs"
};

// src/protocols/op_stack_l1_block/labels.ts
var OP_STACK_L1_BLOCK = {
  protocolId: "op_stack_l1_block",
  displayName: "OP Stack L1Block",
  docs: "https://specs.optimism.io/protocol/deposits.html"
};

// src/protocols/aerodrome/labels.ts
var AERODROME = {
  protocolId: "aerodrome",
  displayName: "Aerodrome / Velodrome",
  docs: "https://aerodrome.finance/docs"
};

// src/protocols/polymarket_ctf_exchange/labels.ts
var POLYMARKET_CTF_EXCHANGE = {
  protocolId: "polymarket_ctf_exchange",
  displayName: "Polymarket CTF Exchange",
  docs: "https://docs.polymarket.com/"
};
var POLYMARKET_COMPONENTS = {
  fill: "PolymarketOrderFill"
};
var POLYMARKET_EVENTS = {
  orderFilled: "OrderFilled",
  ordersMatched: "OrdersMatched",
  tokenRegistered: "TokenRegistered"
};
var POLYMARKET_FIELDS = {
  orderHash: "orderHash",
  maker: "maker",
  taker: "taker",
  side: "side",
  tokenId: "tokenId",
  makerAssetId: "makerAssetId",
  takerAssetId: "takerAssetId",
  makerAmountFilled: "makerAmountFilled",
  takerAmountFilled: "takerAmountFilled",
  fee: "fee",
  takerOrderHash: "takerOrderHash",
  takerOrderMaker: "takerOrderMaker",
  token0: "token0",
  token1: "token1",
  conditionId: "conditionId"
};
var POLYMARKET_LABELS = {
  trader: "Trader",
  counterparty: "Counterparty",
  market: "Market",
  outcome: "Outcome share",
  order: "Order",
  shares: "Shares",
  paid: "Paid",
  received: "Received",
  price: "Price per share",
  fee: "Fee"
};
var POLYMARKET_SIDE = {
  buy: 0,
  sell: 1
};
var POLYMARKET_SIDE_VERBS = {
  buy: {
    title: "Buy prediction-market shares",
    makerGave: POLYMARKET_LABELS.paid,
    makerGot: POLYMARKET_LABELS.shares
  },
  sell: {
    title: "Sell prediction-market shares",
    makerGave: POLYMARKET_LABELS.shares,
    makerGot: POLYMARKET_LABELS.received
  }
};
function polymarketSide(value) {
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : NaN;
  if (n === POLYMARKET_SIDE.buy) return "buy";
  if (n === POLYMARKET_SIDE.sell) return "sell";
  return void 0;
}
function polymarketPrice(side, makerAmountFilled, takerAmountFilled) {
  const maker = _toBig(makerAmountFilled);
  const taker = _toBig(takerAmountFilled);
  if (side === void 0 || maker === void 0 || taker === void 0) return void 0;
  const [collateral, shares] = side === "buy" ? [maker, taker] : [taker, maker];
  if (shares === 0n || collateral === 0n) return void 0;
  const scaled = collateral * 10000n / shares;
  return `${scaled / 10000n}.${String(scaled % 10000n).padStart(4, "0")}`;
}
function _toBig(value) {
  if (typeof value === "bigint") return value;
  if (typeof value === "number" && Number.isFinite(value)) return BigInt(Math.trunc(value));
  if (typeof value === "string" && value.trim() !== "") {
    try {
      return BigInt(value.trim());
    } catch {
      return void 0;
    }
  }
  return void 0;
}

// src/protocols/seaport/labels.ts
var SEAPORT = {
  protocolId: "seaport",
  displayName: "Seaport",
  docs: "https://docs.opensea.io/docs/seaport"
};
var GNOSIS_SAFE_BRAND = {
  id: "gnosis_safe",
  displayName: "Safe",
  docs: "https://docs.safe.global",
  category: "safe",
  icon: Shield
};
var SUSHISWAP_BRAND = {
  id: "sushiswap_v2_factory",
  displayName: "SushiSwap",
  docs: "https://docs.sushi.com/contracts/classic",
  category: "exchange",
  icon: Droplets
};
var DEPLOYER_BRAND_LIST = [
  GNOSIS_SAFE_BRAND,
  SUSHISWAP_BRAND
];

// src/theme/brand/catalog.ts
var STANDARD_BRANDS = {
  [ERC20.protocolId]: {
    id: ERC20.protocolId,
    displayName: ERC20.displayName,
    docs: ERC20.docs,
    category: "token",
    icon: Coins
  },
  [ERC721.protocolId]: {
    id: ERC721.protocolId,
    displayName: ERC721.displayName,
    docs: ERC721.docs,
    category: "nft",
    icon: Image
  },
  [ERC1155.protocolId]: {
    id: ERC1155.protocolId,
    displayName: ERC1155.displayName,
    docs: ERC1155.docs,
    category: "nft",
    // Distinguish multi-token (1155) from single NFTs (721) at a glance.
    icon: Layers
  },
  [NATIVE_TRANSFER.protocolId]: {
    id: NATIVE_TRANSFER.protocolId,
    displayName: NATIVE_TRANSFER.displayName,
    category: "native",
    icon: Coins
  },
  [PRECOMPILE.protocolId]: {
    id: PRECOMPILE.protocolId,
    displayName: PRECOMPILE.displayName,
    category: "precompile",
    icon: Cpu
  },
  [LOWLEVEL.protocolId]: {
    id: LOWLEVEL.protocolId,
    displayName: LOWLEVEL.displayName,
    // Low-level `receive()`/`fallback()` plumbing reads as quiet structure (like
    // proxy delegation), so the neutral grey keeps these frequent cards calm.
    category: "neutral",
    icon: Workflow
  },
  [DEPLOYMENT.protocolId]: {
    id: DEPLOYMENT.protocolId,
    displayName: DEPLOYMENT.displayName,
    category: "deployment",
    icon: FileCode2
  },
  [PROXY.protocolId]: {
    id: PROXY.protocolId,
    displayName: PROXY.displayName,
    category: "proxy",
    icon: Network
  },
  [OWNERSHIP.protocolId]: {
    id: OWNERSHIP.protocolId,
    displayName: OWNERSHIP.displayName,
    category: "ownership",
    icon: KeyRound
  },
  [ACCESS.protocolId]: {
    id: ACCESS.protocolId,
    displayName: ACCESS.displayName,
    category: "access",
    icon: Shield
  },
  [ERC4626.protocolId]: {
    id: ERC4626.protocolId,
    displayName: ERC4626.displayName,
    docs: ERC4626.docs,
    category: "vault",
    icon: Vault
  }
};
var DECODE_PROTOCOL_BRANDS = {
  [CHAINLINK_CCIP.protocolId]: {
    id: CHAINLINK_CCIP.protocolId,
    displayName: CHAINLINK_CCIP.displayName,
    docs: CHAINLINK_CCIP.docs,
    category: "bridge",
    icon: Waypoints,
    // Matches the backend `SceneOffchainSpec.label` for the CCIP DON node.
    graphAliases: ["Chainlink CCIP"]
  },
  [WORMHOLE.protocolId]: {
    id: WORMHOLE.protocolId,
    displayName: WORMHOLE.displayName,
    docs: WORMHOLE.docs,
    category: "bridge",
    // Override the bridge default (blue) so Wormhole reads distinctly from CCIP.
    accent: "violet",
    icon: Radio,
    // Matches the backend `SceneOffchainSpec.label` for the Wormhole attestation node.
    graphAliases: ["Wormhole Guardians"]
  },
  [CHAINLINK_AUTOMATION.protocolId]: {
    id: CHAINLINK_AUTOMATION.protocolId,
    displayName: CHAINLINK_AUTOMATION.displayName,
    docs: CHAINLINK_AUTOMATION.docs,
    category: "oracle",
    // Override the oracle default (violet) so it stays distinct from Wormhole.
    accent: "indigo",
    icon: Cog
  },
  [HYPERLANE.protocolId]: {
    id: HYPERLANE.protocolId,
    displayName: HYPERLANE.displayName,
    docs: HYPERLANE.docs,
    category: "bridge",
    // Hyperlane had no entry at all, so it resolved to the NEUTRAL brand — no
    // accent, no glyph, no name — while every other bridge on the same
    // transaction rendered branded. It is also the protocol this package
    // already treats as most confusable with CCIP: it surfaces its messageId
    // under the SHARED `message_id` kind, and `registerHyperlane` exists in the
    // defaults specifically so CCIP's unscoped hint does not claim it. Being
    // the one bridge with no colour, next to the one it is mistaken for, is the
    // worst place to have this gap.
    //
    // Rose, because blue (CCIP), violet (Wormhole), cyan (Mayan) and indigo
    // (LayerZero, below) are taken by the bridges it appears alongside.
    accent: "rose",
    icon: Network,
    // Matches the backend `SceneOffchainSpec.label` for the Hyperlane validator
    // / relayer node, so the off-chain graph node resolves to this brand too.
    graphAliases: ["Hyperlane", "Hyperlane Validators", "Hyperlane Relayer"]
  },
  [OKX_XBRIDGE.protocolId]: {
    id: OKX_XBRIDGE.protocolId,
    displayName: OKX_XBRIDGE.displayName,
    docs: OKX_XBRIDGE.docs,
    category: "aggregator",
    icon: Shuffle
  },
  [MAYAN.protocolId]: {
    id: MAYAN.protocolId,
    displayName: MAYAN.displayName,
    docs: MAYAN.docs,
    category: "bridge",
    // Override the bridge default (blue, CCIP) so Mayan reads as its turquoise
    // brand and stays distinct from CCIP (blue) + Wormhole (violet); the `Waves`
    // glyph nods to the Mayan ocean motif.
    accent: "cyan",
    icon: Waves,
    // Matches the backend `SceneOffchainSpec.label` for the Mayan solver/driver
    // network node, so the off-chain graph node resolves to this brand.
    graphAliases: ["Mayan Drivers", "Mayan"]
  },
  [LIFI.protocolId]: {
    id: LIFI.protocolId,
    displayName: LIFI.displayName,
    docs: LIFI.docs,
    category: "aggregator",
    // Override the aggregator default (teal, shared with OKX) so LI.FI reads as a
    // warm amber — close to its own orange brand and, more importantly, distinct
    // from the bridge it most often wraps (Mayan, cyan) on the same tx. The
    // `Route` glyph nods to LI.FI routing a transfer across bridges.
    accent: "amber",
    icon: Route
  },
  // The DEX / aggregator family. Uniswap owns exchange pink (below), so each of
  // these takes a distinct accent — they routinely appear on the SAME
  // transaction as Uniswap and as each other (an aggregator routing through a
  // pool), and a shared colour would make "who chose this venue" unreadable.
  [COWSWAP.protocolId]: {
    id: COWSWAP.protocolId,
    displayName: COWSWAP.displayName,
    docs: COWSWAP.docs,
    category: "exchange",
    // CoW's own brand green, and distinct from the pink Uniswap family it
    // settles through.
    accent: "emerald",
    icon: FileSignature,
    // Matches the backend `SceneOffchainSpec.label` for the CoW solver node.
    graphAliases: ["CoW Protocol", "CoW Swap"]
  },
  [ONEINCH.protocolId]: {
    id: ONEINCH.protocolId,
    displayName: ONEINCH.displayName,
    docs: ONEINCH.docs,
    category: "aggregator",
    // Distinct from OKX (teal default) and LI.FI (amber), the two aggregators
    // it most often shares a transaction with.
    accent: "indigo",
    icon: Shuffle,
    graphAliases: ["1inch"]
  },
  [KYBERSWAP.protocolId]: {
    id: KYBERSWAP.protocolId,
    displayName: KYBERSWAP.displayName,
    docs: KYBERSWAP.docs,
    category: "aggregator",
    accent: "cyan",
    icon: Route,
    graphAliases: ["KyberSwap"]
  },
  [PANCAKESWAP.protocolId]: {
    id: PANCAKESWAP.protocolId,
    displayName: PANCAKESWAP.displayName,
    docs: PANCAKESWAP.docs,
    category: "exchange",
    // Amber, and for the same reason SushiSwap gets violet: PancakeSwap's V2
    // router is byte-identical to Uniswap's and its pairs emit the identical
    // event, so the two are told apart by address alone. Its own colour is what
    // makes that distinction visible at a glance rather than only in the badge.
    accent: "amber",
    icon: Cake,
    graphAliases: ["PancakeSwap", "Pancake"]
  },
  [SUSHISWAP.protocolId]: {
    id: SUSHISWAP.protocolId,
    displayName: SUSHISWAP.displayName,
    docs: SUSHISWAP.docs,
    category: "exchange",
    // The accent that matters most in this file. SushiSwap's router is
    // byte-identical to Uniswap's, so the two are told apart by address alone —
    // giving Sushi its own colour is what makes that distinction visible at a
    // glance rather than only in the protocol badge's text.
    accent: "violet",
    icon: Waves,
    graphAliases: ["SushiSwap"]
  },
  [TOKEMAK_MESSAGING.protocolId]: {
    id: TOKEMAK_MESSAGING.protocolId,
    displayName: TOKEMAK_MESSAGING.displayName,
    docs: TOKEMAK_MESSAGING.docs,
    category: "messaging",
    icon: MessageSquareText
  },
  // Uniswap family — every member reads the same exchange pink so the DEX is one
  // recognizable colour across versions, with per-member glyphs (swap / approval
  // signature / liquidity droplets) and display names that disambiguate version.
  [UNISWAP_UNIVERSAL_ROUTER.protocolId]: {
    id: UNISWAP_UNIVERSAL_ROUTER.protocolId,
    displayName: UNISWAP_UNIVERSAL_ROUTER.displayName,
    docs: UNISWAP_UNIVERSAL_ROUTER.docs,
    category: "exchange",
    icon: Repeat,
    // Matches the backend Universal Router semantic node's `attributes.protocol`.
    graphAliases: ["Uniswap Universal Router"]
  },
  [UNISWAP_PERMIT2.protocolId]: {
    id: UNISWAP_PERMIT2.protocolId,
    displayName: UNISWAP_PERMIT2.displayName,
    docs: UNISWAP_PERMIT2.docs,
    category: "exchange",
    icon: FileSignature
  },
  [UNISWAP_V1_FACTORY.protocolId]: {
    id: UNISWAP_V1_FACTORY.protocolId,
    displayName: UNISWAP_V1_FACTORY.displayName,
    docs: UNISWAP_V1_FACTORY.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V1_EXCHANGE.protocolId]: {
    id: UNISWAP_V1_EXCHANGE.protocolId,
    displayName: UNISWAP_V1_EXCHANGE.displayName,
    docs: UNISWAP_V1_EXCHANGE.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V2_ROUTER.protocolId]: {
    id: UNISWAP_V2_ROUTER.protocolId,
    displayName: UNISWAP_V2_ROUTER.displayName,
    docs: UNISWAP_V2_ROUTER.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V3_ROUTER.protocolId]: {
    id: UNISWAP_V3_ROUTER.protocolId,
    displayName: UNISWAP_V3_ROUTER.displayName,
    docs: UNISWAP_V3_ROUTER.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V4_POOL_MANAGER.protocolId]: {
    id: UNISWAP_V4_POOL_MANAGER.protocolId,
    displayName: UNISWAP_V4_POOL_MANAGER.displayName,
    docs: UNISWAP_V4_POOL_MANAGER.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V4_POSITION_MANAGER.protocolId]: {
    id: UNISWAP_V4_POSITION_MANAGER.protocolId,
    displayName: UNISWAP_V4_POSITION_MANAGER.displayName,
    docs: UNISWAP_V4_POSITION_MANAGER.docs,
    category: "exchange",
    icon: Droplets
  },
  [UNISWAP_V3_POSITION_MANAGER.protocolId]: {
    id: UNISWAP_V3_POSITION_MANAGER.protocolId,
    displayName: UNISWAP_V3_POSITION_MANAGER.displayName,
    docs: UNISWAP_V3_POSITION_MANAGER.docs,
    category: "exchange",
    icon: Droplets
  },
  // The pool half of the family: the factories that DEPLOY pools and the pool
  // contracts themselves. Registered because a deployment frame brands from
  // whichever of the two identified it, and an unregistered id degrades to the
  // neutral "Protocol" badge — which would put a grey chip on a pool creation the
  // backend named precisely.
  [UNISWAP_V2_FACTORY.protocolId]: {
    id: UNISWAP_V2_FACTORY.protocolId,
    displayName: UNISWAP_V2_FACTORY.displayName,
    docs: UNISWAP_V2_FACTORY.docs,
    category: "exchange",
    icon: Droplets
  },
  [UNISWAP_V3_FACTORY.protocolId]: {
    id: UNISWAP_V3_FACTORY.protocolId,
    displayName: UNISWAP_V3_FACTORY.displayName,
    docs: UNISWAP_V3_FACTORY.docs,
    category: "exchange",
    icon: Droplets
  },
  [UNISWAP_V2_PAIR.protocolId]: {
    id: UNISWAP_V2_PAIR.protocolId,
    displayName: UNISWAP_V2_PAIR.displayName,
    docs: UNISWAP_V2_PAIR.docs,
    category: "exchange",
    icon: Droplets
  },
  [UNISWAP_V3_POOL.protocolId]: {
    id: UNISWAP_V3_POOL.protocolId,
    displayName: UNISWAP_V3_POOL.displayName,
    docs: UNISWAP_V3_POOL.docs,
    category: "exchange",
    icon: Droplets
  },
  // The POOL half of PancakeSwap, branded separately from its routers because
  // it is a separate protocol id — and without an entry of its own it fell back
  // to the neutral grey badge labelled "Pancakeswap v3 pool".
  [PANCAKESWAP_V3_POOL.protocolId]: {
    id: PANCAKESWAP_V3_POOL.protocolId,
    displayName: PANCAKESWAP_V3_POOL.displayName,
    docs: PANCAKESWAP_V3_POOL.docs,
    category: "exchange",
    // Droplets, matching the Uniswap pools: a pool is a pool whichever fork
    // minted it, and the glyph should say "liquidity venue" before it says
    // which brand.
    icon: Droplets
  },
  // Smart accounts. `safe` on purpose — the accent map already reads that
  // category as "Safe & friends", and an ERC-4337 account answers the same
  // question a multisig does: who controls this, and who may act for it.
  [ERC4337.protocolId]: {
    id: ERC4337.protocolId,
    displayName: ERC4337.displayName,
    docs: ERC4337.docs,
    category: "safe",
    icon: UserCog
  },
  // The MESSAGE layer, not the OFT token bridge — `bridge` to match the
  // definition's own category so the taxonomy reads the same on both sides.
  [LAYERZERO_V2.protocolId]: {
    id: LAYERZERO_V2.protocolId,
    displayName: LAYERZERO_V2.displayName,
    docs: LAYERZERO_V2.docs,
    category: "bridge",
    // Override the bridge default (blue) for the same reason Wormhole and Mayan
    // do: that default IS CCIP's colour, and a message bridge sharing it with
    // the bridge it is most often confused for defeats the point of the accent.
    // This entry was the one bridge that never got its override.
    accent: "indigo",
    icon: BadgeCheck
  },
  // An intents bridge whose destination leg is a solver paying out, so its
  // cards land beside the venues the solver used. A distinct accent from the
  // bridge default matters more here than for most: a Relay fill routinely
  // renders next to a KyberSwap or Uniswap swap in the same transaction, and
  // sharing the generic bridge blue would blur which of the two moved the money.
  [RELAY.protocolId]: {
    id: RELAY.protocolId,
    displayName: RELAY.displayName,
    category: "bridge",
    accent: "emerald",
    icon: ArrowLeftRight
  },
  // The lending position: supply, borrow, repay, liquidate. A bank glyph rather
  // than a coin — what distinguishes Aave from a transfer is the OBLIGATION, not
  // the asset that moved.
  [AAVE_V3.protocolId]: {
    id: AAVE_V3.protocolId,
    displayName: AAVE_V3.displayName,
    docs: AAVE_V3.docs,
    category: "lending",
    icon: Landmark
  },
  // One brand for two chains, mirroring the single backend definition — see the
  // labels module for why the name carries both.
  [AERODROME.protocolId]: {
    id: AERODROME.protocolId,
    displayName: AERODROME.displayName,
    docs: AERODROME.docs,
    category: "exchange",
    icon: Wind
  },
  // The chain's own system contract, not an application. ``precompile`` is the
  // nearest existing category and the right reading: a predeploy at a reserved
  // address is low-level infrastructure, and its cool indigo keeps it visually
  // quiet — which matters when it appears on 100% of blocks and must never
  // compete with the transaction a reader actually opened.
  [OP_STACK_L1_BLOCK.protocolId]: {
    id: OP_STACK_L1_BLOCK.protocolId,
    displayName: OP_STACK_L1_BLOCK.displayName,
    docs: OP_STACK_L1_BLOCK.docs,
    category: "precompile",
    icon: Milestone
  },
  [SEAPORT.protocolId]: {
    id: SEAPORT.protocolId,
    displayName: SEAPORT.displayName,
    docs: SEAPORT.docs,
    category: "nft",
    icon: Ship
  },
  [CONDITIONAL_TOKENS.protocolId]: {
    id: CONDITIONAL_TOKENS.protocolId,
    displayName: CONDITIONAL_TOKENS.displayName,
    docs: CONDITIONAL_TOKENS.docs,
    category: "derivatives",
    icon: Scale
  },
  // The venue, beside the standard it trades. A different glyph on purpose:
  // the two appear on the SAME transaction constantly — a settlement that
  // splits collateral to fill an order carries both — and one scale icon over
  // both would leave a reader unable to tell the mint from the trade.
  [POLYMARKET_CTF_EXCHANGE.protocolId]: {
    id: POLYMARKET_CTF_EXCHANGE.protocolId,
    displayName: POLYMARKET_CTF_EXCHANGE.displayName,
    docs: POLYMARKET_CTF_EXCHANGE.docs,
    category: "derivatives",
    icon: CandlestickChart
  }
};
var DEPLOYER_BRANDS = Object.fromEntries(
  DEPLOYER_BRAND_LIST.map((brand) => [brand.id, brand])
);
var PROTOCOL_BRANDS = {
  ...DECODE_PROTOCOL_BRANDS,
  ...DEPLOYER_BRANDS,
  ...STANDARD_BRANDS
};
var CATEGORY_ICON = {
  bridge: ArrowLeftRight,
  oracle: Radar,
  messaging: MessageSquareText,
  aggregator: Shuffle,
  exchange: Repeat,
  lending: Landmark,
  token: Coins,
  nft: Image,
  native: Coins,
  precompile: Cpu,
  deployment: FileCode2,
  proxy: Network,
  ownership: KeyRound,
  access: Shield,
  vault: Vault,
  safe: ShieldCheck,
  derivatives: CandlestickChart,
  infrastructure: Server,
  privacy: EyeOff,
  mev: Bot,
  neutral: void 0
};
var CATALOG_BRAND_CATEGORY = {
  bridge: "bridge",
  exchange: "exchange",
  aggregator: "aggregator",
  lending: "lending",
  flashloan: "lending",
  derivatives: "derivatives",
  safe: "safe",
  account_abstraction: "safe",
  governance: "ownership",
  oracle: "oracle",
  infrastructure: "infrastructure",
  erc20: "token",
  nft: "nft",
  staking: "vault",
  // A searcher's own program: it CALLS the venues rather than being one, so it
  // reads as itself and never as the exchange it traded against.
  mev: "mev",
  // An exploit is a finding about a transaction, not a venue's colour.
  exploit: "neutral",
  privacy: "privacy",
  other: "neutral"
};
var DERIVED = /* @__PURE__ */ new Map();
function cataloguedBrand(protocolId) {
  const cached = DERIVED.get(protocolId);
  if (cached !== void 0) return cached ?? void 0;
  const displayName = protocolDisplayName(protocolId);
  const catalogued = protocolCategory(protocolId);
  const category = catalogued ? CATALOG_BRAND_CATEGORY[catalogued] : void 0;
  const brand = displayName && category ? { id: protocolId, displayName, category, icon: CATEGORY_ICON[category] } : null;
  DERIVED.set(protocolId, brand);
  return brand ?? void 0;
}
var NEUTRAL_BRAND = {
  id: "neutral",
  displayName: "Protocol",
  category: "neutral"
};
function brandForProtocol(protocolId) {
  if (!protocolId) return NEUTRAL_BRAND;
  return PROTOCOL_BRANDS[protocolId] ?? cataloguedBrand(protocolId) ?? NEUTRAL_BRAND;
}
function protocolName(protocolId) {
  if (!protocolId) return void 0;
  const brand = brandForProtocol(protocolId);
  return brand !== NEUTRAL_BRAND ? brand.displayName : protocolDisplayName(protocolId);
}
function brandForKind(kind) {
  if (!kind) return NEUTRAL_BRAND;
  const dot = kind.indexOf(".");
  if (dot <= 0) return NEUTRAL_BRAND;
  return brandForProtocol(kind.slice(0, dot));
}
function brandForGraphLabel(label) {
  if (!label) return void 0;
  const needle = label.trim().toLowerCase();
  if (!needle) return void 0;
  for (const brand of Object.values(PROTOCOL_BRANDS)) {
    if (brand.displayName.toLowerCase() === needle) return brand;
    if (brand.graphAliases?.some((alias) => alias.toLowerCase() === needle)) return brand;
  }
  return void 0;
}
function accentClasses(brand) {
  return ACCENTS[brand.accent ?? CATEGORY_ACCENT[brand.category]];
}
function accentForProtocol(protocolId) {
  return accentClasses(brandForProtocol(protocolId));
}
function overrideFor(map, protocolId, chainId) {
  const entry = map?.[protocolId];
  if (!entry) return void 0;
  const { perChain, ...base } = entry;
  const chain = chainId ? perChain?.[chainId] : void 0;
  return chain ? { ...base, ...chain } : base;
}
function resolveBrand(base, layers, chainId) {
  const tenant = overrideFor(layers.tenant, base.id, chainId);
  const user = overrideFor(layers.user, base.id, chainId);
  if (!tenant && !user) return base;
  const accent = resolveScalar({
    user: user?.accent,
    tenant: tenant?.accent,
    default: base.accent
  });
  const displayName = resolveScalar({ user: user?.label, tenant: tenant?.label, default: base.displayName }) ?? base.displayName;
  const hidden = resolveScalar({ user: user?.hidden, tenant: tenant?.hidden });
  return { ...base, accent, displayName, hidden };
}
var BrandContext = React.createContext({});
function BrandProvider({
  tenant,
  user,
  chain,
  children
}) {
  const value = React.useMemo(() => ({ tenant, user, chain }), [tenant, user, chain]);
  return /* @__PURE__ */ jsx(BrandContext.Provider, { value, children });
}
function useBrandOverrides() {
  return React.useContext(BrandContext);
}
function useBrand(protocolId, chainId) {
  const { tenant, user, chain } = React.useContext(BrandContext);
  const effectiveChain = chainId ?? chain;
  return React.useMemo(
    () => resolveBrand(brandForProtocol(protocolId), { tenant, user }, effectiveChain),
    [protocolId, tenant, user, effectiveChain]
  );
}
function useBrandAccent(protocolId, chainId) {
  return accentClasses(useBrand(protocolId, chainId));
}

// src/theme/brand/storage.ts
var BRAND_OVERRIDES_KEY = "brand:overrides";
var BrandPrefsStore = class _BrandPrefsStore {
  constructor(storage) {
    this.storage = storage;
  }
  storage;
  /** Derive a store for a different identity (e.g. on tenant/user switch). */
  with(patch) {
    return new _BrandPrefsStore(this.storage.with(patch));
  }
  /** Hierarchical read of the override map (most specific scope wins). */
  getOverrides() {
    return this.storage.get(BRAND_OVERRIDES_KEY, {});
  }
  /** Set/replace one protocol's override at `level` (default: the storage's). */
  setOverride(protocolId, override, level = this.storage.defaultLevel) {
    const current = this.storage.getAt(BRAND_OVERRIDES_KEY, level, {});
    this.storage.set(BRAND_OVERRIDES_KEY, { ...current, [protocolId]: override }, level);
  }
  /** Remove one protocol's override at `level`. */
  removeOverride(protocolId, level = this.storage.defaultLevel) {
    const current = this.storage.getAt(BRAND_OVERRIDES_KEY, level, {});
    if (!(protocolId in current)) return;
    const { [protocolId]: _removed, ...rest } = current;
    this.storage.set(BRAND_OVERRIDES_KEY, rest, level);
  }
};
function createBrandPrefsStore(storage) {
  return new BrandPrefsStore(storage);
}
var CardFooterContext = React.createContext(null);
function CardFooterProvider({
  footer,
  children
}) {
  return /* @__PURE__ */ jsx(CardFooterContext.Provider, { value: footer, children });
}
function useCardFooter() {
  return React.useContext(CardFooterContext);
}
function SectionCard({
  title,
  meta,
  protocolId,
  children,
  className
}) {
  const accent = useBrandAccent(protocolId);
  const footer = useCardFooter();
  return /* @__PURE__ */ jsx(CardFooterProvider, { footer: null, children: /* @__PURE__ */ jsxs("div", { className: cn(PROTOCOL_CARD, protocolId && ["border-l-2", accent.bar], className), children: [
    /* @__PURE__ */ jsxs("div", { className: PROTOCOL_CARD_HEADER, children: [
      title,
      meta != null && /* @__PURE__ */ jsx("div", { className: "ml-auto flex items-center gap-2", children: meta })
    ] }),
    /* @__PURE__ */ jsx("div", { className: PROTOCOL_CARD_BODY, children }),
    footer != null && /* @__PURE__ */ jsx("div", { className: cn(PROTOCOL_CARD_BODY, "border-t border-border/60"), children: footer })
  ] }) });
}
var MAX_FIELD_DEPTH = 5;
function legField(leg, ...names) {
  const rec = decodedOrRaw(leg);
  if (!isRecord(rec)) return void 0;
  for (const name of names) {
    const target = name.toLowerCase();
    for (const [key, value] of Object.entries(rec)) {
      if (key.toLowerCase() === target) return value;
    }
  }
  return void 0;
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function normName(name) {
  return name.toLowerCase().replace(/^_+|_+$/g, "");
}
function decodedOrRaw(value) {
  return decodedOf(value) ?? rawOf(value);
}
function container(value) {
  const v = decodedOrRaw(value);
  if (Array.isArray(v)) return v;
  if (isRecord(v)) return v;
  return void 0;
}
function findInValue(value, target, depth) {
  const c = container(value);
  if (c === void 0) return void 0;
  if (!Array.isArray(c)) {
    for (const [k, v] of Object.entries(c)) {
      if (normName(k) === target) return v;
    }
    if (depth >= MAX_FIELD_DEPTH) return void 0;
    for (const v of Object.values(c)) {
      const found = findInValue(v, target, depth + 1);
      if (found !== void 0) return found;
    }
    return void 0;
  }
  if (depth >= MAX_FIELD_DEPTH) return void 0;
  for (const v of c) {
    const found = findInValue(v, target, depth + 1);
    if (found !== void 0) return found;
  }
  return void 0;
}
function pickField(params, ...names) {
  const targets = names.map(normName);
  for (const target of targets) {
    for (const p of params) {
      if (p.name && normName(p.name) === target) return p.value;
    }
  }
  for (const target of targets) {
    for (const p of params) {
      const found = findInValue(p.value, target, 0);
      if (found !== void 0) return found;
    }
  }
  return void 0;
}
function pickSemantic(params, role, ...fallbackAliases) {
  for (const p of params) {
    if (p.canonicalName && p.canonicalName.toLowerCase() === role.toLowerCase()) return p.value;
  }
  return pickField(params, role, ...fallbackAliases);
}
function pickFrom(value, ...names) {
  for (const name of names) {
    const found = findInValue(value, normName(name), 0);
    if (found !== void 0) return found;
  }
  return void 0;
}
function hasBytesPayload(value) {
  const v = isEnriched(value) ? rawOf(value) : value;
  if (typeof v !== "string") return v != null;
  const hex = v.startsWith("0x") ? v.slice(2) : v;
  return hex.length > 0;
}
var BADGE_BASE = "inline-flex items-center gap-1 rounded px-1.5 py-px text-[11px] font-medium";
function ProtocolBadge({
  protocolId,
  label,
  hideIcon,
  className
}) {
  const brand = useBrand(protocolId);
  if (brand.hidden && label == null) return null;
  const accent = accentClasses(brand);
  const Icon = brand.icon;
  const text = label ?? (protocolId && brand.id === "neutral" ? protocolName(protocolId) ?? humanizeFieldName(protocolId) : brand.displayName);
  return /* @__PURE__ */ jsxs("span", { className: cn(BADGE_BASE, accent.badge, className), children: [
    !hideIcon && Icon ? /* @__PURE__ */ jsx(Icon, { className: "h-3 w-3 shrink-0", "aria-hidden": true }) : null,
    text
  ] });
}
function ProtocolIcon({ protocolId, icon: Icon, className }) {
  const accent = useBrandAccent(protocolId);
  return /* @__PURE__ */ jsx(Icon, { className: cn("h-4 w-4 shrink-0", accent.text, className), "aria-hidden": true });
}
function isFactVisible(f) {
  return "when" in f ? Boolean(f.when) : f.value != null && f.value !== "";
}
function FactGrid({ facts, className }) {
  const shown = facts.filter(isFactVisible);
  return /* @__PURE__ */ jsx(
    "dl",
    {
      className: ["grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[max-content_1fr]", className].filter(Boolean).join(" "),
      children: shown.map((f) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
        /* @__PURE__ */ jsx("dt", { className: FACT_LABEL, children: f.label }),
        /* @__PURE__ */ jsx("dd", { className: FACT_VALUE, children: f.value })
      ] }, f.label))
    }
  );
}
function defaultHintsForChain(defaults, chain) {
  const abiDecoded = chainDecodeVocabulary(chain).schemaRegistry;
  if (abiDecoded) return defaults;
  const scoped = (hints) => hints.filter((h) => h.protocolId);
  return {
    valueHints: scoped(defaults.valueHints),
    eventHints: scoped(defaults.eventHints),
    callHints: scoped(defaults.callHints)
  };
}
function valueHintForKind(hints, kind) {
  if (!hints || !kind) return void 0;
  return hints.valueHints.find((h) => h.kind === kind);
}
function valueHintForKindScoped(hints, kind, protocolId) {
  if (!hints || !kind) return void 0;
  const ofKind = hints.valueHints.filter((h) => h.kind === kind);
  const scoped = protocolId ? ofKind.find(
    (h) => h.protocolId && h.protocolId.toLowerCase() === protocolId.toLowerCase()
  ) : void 0;
  return scoped ?? ofKind.find((h) => !h.protocolId || !protocolId);
}
function eventHintFor(hints, protocolIds, event) {
  if (!hints || !event.name) return void 0;
  const present = new Set(protocolIds.map((p) => p.toLowerCase()));
  const candidates = hints.eventHints.filter((h) => {
    if (h.event !== event.name) return false;
    if (h.signature && event.signature && h.signature !== event.signature) return false;
    if (h.protocolId && !present.has(h.protocolId.toLowerCase())) return false;
    return true;
  });
  return preferHint(candidates, event.signature, protocolIds, (h) => h.signature, (h) => h.protocolId);
}
function callHintFor(hints, protocolIds, call) {
  if (!hints || !call.name) return void 0;
  const present = new Set(protocolIds.map((p) => p.toLowerCase()));
  const candidates = hints.callHints.filter((h) => {
    if (h.call !== call.name) return false;
    if (h.signature && call.signature && h.signature !== call.signature) return false;
    if (h.protocolId && !present.has(h.protocolId.toLowerCase())) return false;
    return true;
  });
  return preferHint(candidates, call.signature, protocolIds, (h) => h.signature, (h) => h.protocolId);
}
function preferHint(candidates, signature, protocolIds, signatureOf, protocolOf) {
  if (candidates.length <= 1) return candidates[0];
  const exact = signature ? candidates.filter((h) => signatureOf(h) === signature) : [];
  const nameOnly = candidates.filter((h) => !signatureOf(h));
  const narrowed = exact.length > 0 ? exact : nameOnly.length > 0 ? nameOnly : candidates;
  const [first, ...rest] = narrowed;
  if (first === void 0) return void 0;
  if (rest.length === 0) return first;
  const rank = protocolIds.map((p) => p.toLowerCase());
  const precedence = (h) => {
    const pid = protocolOf(h);
    if (!pid) return Number.MAX_SAFE_INTEGER;
    const at = rank.indexOf(pid.toLowerCase());
    return at === -1 ? Number.MAX_SAFE_INTEGER : at;
  };
  return rest.reduce((best, h) => precedence(h) < precedence(best) ? h : best, first);
}
var ProtocolErrorBoundary = class extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
};
function ProtocolBoundary({ fallback, pending, children }) {
  return /* @__PURE__ */ jsx(ProtocolErrorBoundary, { fallback, children: /* @__PURE__ */ jsx(React.Suspense, { fallback: pending ?? fallback, children }) });
}
var EMPTY_HINTS = { valueHints: [], eventHints: [], callHints: [] };
var DEFAULT_CONTEXT = {
  registry: createProtocolUiRegistry(),
  hints: EMPTY_HINTS,
  protocolIds: []
};
var ProtocolUiContext = React.createContext(DEFAULT_CONTEXT);
function mergeHints(body, fallback) {
  return {
    valueHints: [...body.valueHints, ...fallback.valueHints],
    eventHints: [...body.eventHints, ...fallback.eventHints],
    callHints: [...body.callHints, ...fallback.callHints]
  };
}
function ProtocolUiProvider({
  registry,
  hints,
  protocolIds,
  primaryProtocolId,
  chain,
  amountHidden,
  renderAddress,
  onAddressDrilldown,
  children
}) {
  const value = React.useMemo(
    () => ({
      registry,
      hints: mergeHints(hints ?? EMPTY_HINTS, defaultHintsForChain(registry.defaultHints(), chain)),
      protocolIds: protocolIds ?? [],
      primaryProtocolId,
      chain,
      amountHidden,
      renderAddress,
      onAddressDrilldown
    }),
    [registry, hints, protocolIds, primaryProtocolId, chain, amountHidden, renderAddress, onAddressDrilldown]
  );
  return /* @__PURE__ */ jsx(ProtocolUiContext.Provider, { value, children });
}
function useProtocolUi() {
  return React.useContext(ProtocolUiContext);
}
function useDecodedKindRenderer() {
  const { registry, hints, protocolIds, chain, renderAddress, onAddressDrilldown } = useProtocolUi();
  return React.useCallback(
    function renderKind(info) {
      const valueChain = info.chain ?? chain;
      if (info.kind === "call") {
        const call = parseDecodedCallValue(info.decoded);
        const callHint = call ? callHintFor(hints, protocolIds, { name: call.label, signature: call.signature }) : void 0;
        const CallComponent = registry.callComponent(callHint?.component);
        if (call && callHint && CallComponent) {
          const generic = /* @__PURE__ */ jsx(
            CallView,
            {
              decoded: info.decoded,
              raw: info.raw,
              chain: valueChain,
              renderAddress,
              onAddressDrilldown,
              renderKind,
              depth: info.depth
            }
          );
          return /* @__PURE__ */ jsx(ProtocolBoundary, { fallback: generic, children: /* @__PURE__ */ jsx(
            CallComponent,
            {
              call,
              hint: callHint,
              chain: valueChain,
              renderAddress,
              onAddressDrilldown
            }
          ) });
        }
        return void 0;
      }
      const hint = valueHintForKind(hints, info.kind);
      const Component2 = registry.valueComponent(hint?.component);
      if (!Component2) return void 0;
      return /* @__PURE__ */ jsx(ProtocolBoundary, { fallback: null, children: /* @__PURE__ */ jsx(
        Component2,
        {
          kind: info.kind,
          display: info.display,
          raw: info.raw,
          decoded: info.decoded,
          chain: valueChain,
          hint,
          renderAddress,
          onAddressDrilldown
        }
      ) });
    },
    [registry, hints, protocolIds, chain, renderAddress, onAddressDrilldown]
  );
}
function ProtocolValue({ value, chain, type, className }) {
  const context = useProtocolUi();
  const { renderAddress, onAddressDrilldown } = context;
  const renderKind = useDecodedKindRenderer();
  return /* @__PURE__ */ jsx(
    DecodedValue,
    {
      value,
      type,
      chain: chain ?? context.chain,
      renderAddress,
      onAddressDrilldown,
      renderKind,
      className
    }
  );
}

export { ACCENTS, ACCENT_TEXT, ACCESS, AUTOMATION_COMPONENTS, AUTOMATION_FIELD_LABELS, BRAND_OVERRIDES_KEY, BrandPrefsStore, BrandProvider, CATALOG_BRAND_CATEGORY, CATEGORY_ACCENT, CATEGORY_ICON, CCIP_COMPONENTS, CCIP_CURSE_SUBJECT_KIND, CCIP_FIELD_LABELS, CCIP_MESSAGE_ID_KIND, CCIP_RECEIPT_ROLE_KIND, CCIP_RECEIPT_ROLE_LABELS, CCIP_TOKEN_TRANSFERS_KIND, CCIP_VERIFICATION_KIND, CHAINLINK_AUTOMATION, CHAINLINK_CCIP, CODE_HASH_PARAM, CONDITIONAL_TOKENS, CONDITIONAL_TOKENS_COMPONENTS, CONDITIONAL_TOKENS_EVENTS, CONDITIONAL_TOKENS_FIELDS, CONDITIONAL_TOKENS_LABELS, CONDITIONAL_TOKENS_VERBS, CONTRACT_CREATION_CALL, CONTRACT_TYPE_PARAM, COWSWAP, COWSWAP_CALLS, COWSWAP_COMPONENTS, COWSWAP_EVENTS, COWSWAP_FIELD_LABELS, COWSWAP_KINDS, CardFooterProvider, DEPLOYMENT, ERC1155, ERC20, ERC4337, ERC4337_CALLS, ERC4337_COMPONENTS, ERC4337_EVENTS, ERC4337_FIELDS, ERC4337_LABELS, ERC4626, ERC721, FACT_LABEL, FACT_VALUE, FEE_TIER_PARAM, FUNDED_WITH_PARAM, FactGrid, IDENTIFIED_BY, IDENTIFIED_BY_PARAM, INITIAL_SUPPLY_PARAM, INIT_CODE_PARAM, KYBERSWAP, KYBERSWAP_CALLS, KYBERSWAP_CLIENT_DATA_FIELDS, KYBERSWAP_COMPONENTS, KYBERSWAP_EVENTS, KYBERSWAP_FIELD_LABELS, KYBERSWAP_KINDS, LAYERZERO_V2, LAYERZERO_V2_COMPONENTS, LAYERZERO_V2_EVENTS, LAYERZERO_V2_FIELDS, LAYERZERO_V2_LABELS, LIFI, LIFI_BRIDGE_CALLS, LIFI_COMPONENTS, LIFI_FIELD_LABELS, LOWLEVEL, LOWLEVEL_CALLS, LOWLEVEL_COMPONENT, MAYAN, MAYAN_COMPONENTS, MAYAN_EXECUTOR_CALLS, MAYAN_FIELD_LABELS, MAYAN_FORWARDER_CALLS, MAYAN_MESSAGE_ID_KIND, MAYAN_ORDER_EVENTS, MAYAN_PACKED_PAYLOAD_KIND, MAYAN_SWIFT_CALLS, MAYAN_SWIFT_MESSAGE_KIND, MINTED_TO_PARAM, NATIVE_TRANSFER, NEUTRAL_BRAND, OKX_COMPONENTS, OKX_FIELD_LABELS, OKX_PACKED_PAYLOAD_KIND, OKX_XBRIDGE, ONEINCH, ONEINCH_CALLS, ONEINCH_COMPONENTS, ONEINCH_EVENTS, ONEINCH_FIELDS, ONEINCH_KINDS, OWNERSHIP, OWNERSHIP_RENOUNCED_PARAM, OWNER_PARAM, PANCAKESWAP, PANCAKESWAP_CALLS, PANCAKESWAP_COMPONENTS, PANCAKESWAP_EVENTS, PANCAKESWAP_FIELDS, PANCAKESWAP_KINDS, PANCAKESWAP_V3_POOL, PANCAKESWAP_V3_POOL_COMPONENTS, PANCAKESWAP_V3_POOL_EVENTS, PANCAKESWAP_V3_POOL_FIELDS, PANCAKESWAP_V3_POOL_LABELS, POLYMARKET_COMPONENTS, POLYMARKET_CTF_EXCHANGE, POLYMARKET_EVENTS, POLYMARKET_FIELDS, POLYMARKET_LABELS, POLYMARKET_SIDE_VERBS, PRECOMPILE, PRECOMPILES, PRECOMPILE_COMPONENT, PROTOCOL_BRANDS, PROTOCOL_CARD, PROTOCOL_CARD_BODY, PROTOCOL_CARD_HEADER, PROTOCOL_ID_PARAM, PROTOCOL_TAG, PROXY, ProtocolBadge, ProtocolBoundary, ProtocolIcon, ProtocolUiProvider, ProtocolUiRegistry, ProtocolValue, RELAY, RELAY_COMPONENTS, RELAY_EVENTS, RELAY_FIELDS, RELAY_LABELS, STANDARD_BRANDS, SUSHISWAP, SUSHISWAP_CALLS, SUSHISWAP_COMPONENTS, SUSHISWAP_EVENTS, SUSHISWAP_KINDS, SectionCard, TICK_SPACING_PARAM, TOKEMAK_COMPONENTS, TOKEMAK_FIELD_LABELS, TOKEMAK_MESSAGING, TOKEN0_PARAM, TOKEN1_PARAM, UNISWAP_BRAND_LIST, UNISWAP_COMMAND_LABELS, UNISWAP_COMPONENTS, UNISWAP_FIELD_LABELS, UNISWAP_PERMIT2, UNISWAP_SWAP_COMMANDS, UNISWAP_UNIVERSAL_ROUTER, UNISWAP_V1_EXCHANGE, UNISWAP_V1_FACTORY, UNISWAP_V2_FACTORY, UNISWAP_V2_PAIR, UNISWAP_V2_ROUTER, UNISWAP_V3_FACTORY, UNISWAP_V3_POOL, UNISWAP_V3_POSITION_MANAGER, UNISWAP_V3_ROUTER, UNISWAP_V4_POOL_MANAGER, UNISWAP_V4_POSITION_MANAGER, VAULT_COMPONENTS, WORMHOLE, WORMHOLE_COMPONENTS, WORMHOLE_EXECUTOR_FIELD_LABELS, WORMHOLE_EXECUTOR_REQUEST_KIND, WORMHOLE_FIELD_LABELS, WORMHOLE_MESSAGE_ID_KIND, WORMHOLE_QUOTE_FIELD_LABELS, WORMHOLE_RELAY_INSTRUCTIONS_KIND, WORMHOLE_REQUEST_PREFIX_LABELS, WORMHOLE_SIGNED_QUOTE_KIND, WORMHOLE_TRANSFER_PAYLOAD_KIND, WORMHOLE_VAA_ID_KIND, WORMHOLE_VAA_KIND, WORMHOLE_VM_FIELD_LABELS, accentClasses, accentForProtocol, brandForGraphLabel, brandForKind, brandForProtocol, callHintFor, cataloguedBrand, createBrandPrefsStore, createProtocolUiRegistry, decodedOrRaw, defaultHintsForChain, eventHintFor, hasBytesPayload, isFactVisible, isRecord, legField, pickField, pickFrom, pickSemantic, polymarketPrice, polymarketSide, protocolName, resolveBrand, useBrand, useBrandAccent, useBrandOverrides, useCardFooter, useDecodedKindRenderer, useProtocolUi, valueHintForKind, valueHintForKindScoped };
//# sourceMappingURL=chunk-PJW62ZTR.js.map
//# sourceMappingURL=chunk-PJW62ZTR.js.map